import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../utils/config";

const ENDPOINT_BASE = `${API_BASE_URL}/api/salesUpload`; // /preview and /commit live here

// --------------------
// Simple fuzzy-ish matcher
// --------------------
const normalize = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\W_]+/g, " ") // strip punctuation
    .replace(/\s+/g, " ") // collapse spaces
    .trim();

const tokenScore = (a, b) => {
  const A = normalize(a).split(" ");
  const B = normalize(b).split(" ");
  if (!A[0] || !B[0]) return 0;
  const common = A.filter((t) => B.includes(t)).length;
  return common / Math.max(A.length, B.length);
};

const scoreCandidate = (candidateName, targetName) => {
  const n = normalize(candidateName);
  const t = normalize(targetName);
  if (!n || !t) return 0;
  if (n === t) return 1; // exact match after normalization
  if (n.startsWith(t) || t.startsWith(n)) return 0.95;
  if (n.includes(t) || t.includes(n)) return 0.9;
  return tokenScore(candidateName, targetName);
};

const matchCustomer = (parsedBuyerName, customers, threshold = 0.75) => {
  let best = null,
    bestScore = 0;
  for (const c of customers) {
    const s = scoreCandidate(c.name, parsedBuyerName);
    if (s > bestScore) {
      best = c;
      bestScore = s;
    }
  }
  return bestScore >= threshold ? { best, score: bestScore } : { best: null, score: bestScore };
};

export default function SalesUpload() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  // parsed buyer + match info
  const [parsedBuyer, setParsedBuyer] = useState("");
  const [matchInfo, setMatchInfo] = useState({ best: null, score: 0 });

  const [form, setForm] = useState({
    customer_id: "",
    invoice_number: "",
    invoice_date: "",
    created_by: 4, // your default
  });

  const token = localStorage.getItem("token");

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/customers/rahultemp`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCustomers(res.data || []);
      } catch (err) {
        console.error("Error fetching customers", err);
      }
    };
    fetchCustomers();
  }, [token]);

  const handleFileChange = (e) => {
    const uploaded = e.target.files?.[0];
    if (!uploaded) return;
    // We expect a JSON file (e.g., "sales order.json")
    if (
      uploaded.type !== "application/json" &&
      !/\.json$/i.test(uploaded.name)
    ) {
      setError("Please upload a JSON file (parsed salesorder.json).");
      setFile(null);
      return;
    }
    setError("");
    setFile(uploaded);
    setPreview(null);
    setParsedBuyer("");
    setMatchInfo({ best: null, score: 0 });
    // don't reset customers
  };

  const handlePreview = async () => {
    if (!file) return alert("Select a JSON file first");
    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${ENDPOINT_BASE}/preview`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setPreview(res.data);
      console.log(res.data);

      // auto-fill invoice fields
      setForm((prev) => ({
        ...prev,
        invoice_number: res.data?.header?.invoice_number || "",
        invoice_date: res.data?.header?.invoice_date || "", // "YYYY-MM-DD"
      }));

      // capture parsed buyer for matching
      const buyerName = res.data?.header?.buyer?.name || "";
      setParsedBuyer(buyerName);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  // Auto-select customer once we have parsedBuyer + customer list
  useEffect(() => {
    if (!parsedBuyer || parsedBuyer.toLowerCase() === "unknown" || customers.length === 0) {
      setMatchInfo({ best: null, score: 0 });
      return;
    }
    const { best, score } = matchCustomer(parsedBuyer, customers, 0.75);
    setMatchInfo({ best, score });

    // auto-select only if confident
    if (best) {
      setForm((prev) => ({ ...prev, customer_id: String(best.id) }));
    }
  }, [parsedBuyer, customers]);

  const handleCommit = async () => {
    if (!file) return alert("No file selected");
    if (!preview?.items?.length) return alert("No parsed items to commit");
    if (!form.customer_id) return alert("Select a customer");

    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append("file", file); // backend reads and parses JSON from file
    formData.append("customer_id", form.customer_id);
    formData.append("created_by", String(form.created_by));
    if (form.invoice_date) formData.append("invoice_date", form.invoice_date);
    if (form.invoice_number) formData.append("invoice_number", form.invoice_number); // backend may use/ignore

    try {
      const res = await axios.post(`${ENDPOINT_BASE}/commit`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      alert(`Committed ✓ (invoice #${res.data.invoiceId}, items: ${res.data.items})`);
      setPreview(null);
      setFile(null);
      setParsedBuyer("");
      setMatchInfo({ best: null, score: 0 });
      setForm({ customer_id: "", invoice_number: "", invoice_date: "", created_by: 4 });
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Commit failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <h2 className="text-xl font-bold mb-4">Upload Sales Invoice (JSON)</h2>

      <div className="flex items-center gap-3">
        <input type="file" accept=".json,application/json" onChange={handleFileChange} />
        <button
          onClick={handlePreview}
          disabled={!file || loading}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          {loading ? "Processing..." : "Preview Line Items"}
        </button>
      </div>
      {error && <p className="text-red-600 mt-2">{error}</p>}

      {preview && (
        <>
          {/* Header summary */}
          <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm">
            <div className="border rounded p-3">
              <div className="font-semibold mb-1">Invoice</div>
              <div><b>No:</b> {preview.header?.invoice_number || "-"}</div>
              <div><b>Date:</b> {preview.header?.invoice_date || "-"}</div>
              <div><b>Buyer:</b> {preview.header?.buyer?.name || "-"}</div>
              <div><b>Seller:</b> {preview.header?.seller?.name || "-"}</div>
            </div>
            <div className="border rounded p-3">
              <div className="font-semibold mb-1">Totals (computed)</div>
              <div>Taxable: {preview.totals?.computed?.taxable_sum ?? "-"}</div>
              <div>GST: {preview.totals?.computed?.gst_total ?? "-"}</div>
              <div>Net: {preview.totals?.computed?.net_amount ?? "-"}</div>
            </div>
            <div className="border rounded p-3">
              <div className="font-semibold mb-1">Totals (from JSON)</div>
              <div>Taxable: {preview.totals?.from_json?.taxable_sum ?? "-"}</div>
              <div>GST: {preview.totals?.from_json?.gst_total ?? "-"}</div>
              <div>Net: {preview.totals?.from_json?.net_amount ?? "-"}</div>
              <div>Round Off: {preview.totals?.from_json?.round_off ?? "-"}</div>
            </div>
          </div>

          {/* Buyer → customer match banner */}
          {parsedBuyer && (
            <div className="mt-4 mb-2 p-2 rounded border bg-gray-50 text-sm flex flex-wrap items-center gap-2">
              <span className="text-gray-700">
                <b>Parsed buyer:</b> {parsedBuyer}
              </span>

              {matchInfo.best ? (
                <>
                  <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">
                    Auto-selected: {matchInfo.best.name} (score {matchInfo.score.toFixed(2)})
                  </span>
                  {String(form.customer_id) !== String(matchInfo.best.id) && (
                    <button
                      type="button"
                      onClick={() =>
                        setForm((p) => ({ ...p, customer_id: String(matchInfo.best.id) }))
                      }
                      className="px-2 py-1 rounded bg-green-600 text-white"
                    >
                      Select matched customer
                    </button>
                  )}
                </>
              ) : (
                <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">
                  No confident match. Please choose from the list.
                </span>
              )}
            </div>
          )}

          {/* Items table */}
          <div className="mt-4 overflow-auto">
            <h3 className="font-medium mb-2">Line Items ({preview.items.length})</h3>
            <table className="min-w-full text-sm border">
              <thead className="bg-gray-100">
                <tr>
                  {[
                    "sno",
                    "description",
                    "pack",
                    "hsn",
                    "batch_no",
                    "expiry",
                    "mrp",
                    "rate",
                    "qty",
                    "free_qty",
                    "discount_percent",
                    "gst_percent",
                    "taxable",
                    "gst_amount",
                    "total_line",
                  ].map((k) => (
                    <th key={k} className="border px-2 py-1 text-left">
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.items.map((row, i) => (
                  <tr key={i}>
                    <td className="border px-2 py-1">{row.sno}</td>
                    <td className="border px-2 py-1">{row.description}</td>
                    <td className="border px-2 py-1">{row.pack}</td>
                    <td className="border px-2 py-1">{row.hsn}</td>
                    <td className="border px-2 py-1">{row.batch_no}</td>
                    <td className="border px-2 py-1">{row.expiry}</td>
                    <td className="border px-2 py-1">{row.mrp}</td>
                    <td className="border px-2 py-1">{row.rate}</td>
                    <td className="border px-2 py-1">{row.qty}</td>
                    <td className="border px-2 py-1">{row.free_qty}</td>
                    <td className="border px-2 py-1">{row.discount_percent}</td>
                    <td className="border px-2 py-1">{row.gst_percent}</td>
                    <td className="border px-2 py-1">{row.taxable}</td>
                    <td className="border px-2 py-1">{row.gst_amount}</td>
                    <td className="border px-2 py-1">{row.total_line}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50">
                  <td className="border px-2 py-1 font-semibold" colSpan={12}>
                    Totals (computed)
                  </td>
                  <td className="border px-2 py-1 font-semibold">
                    {preview.totals?.computed?.taxable_sum ?? "-"}
                  </td>
                  <td className="border px-2 py-1 font-semibold">
                    {preview.totals?.computed?.gst_total ?? "-"}
                  </td>
                  <td className="border px-2 py-1 font-semibold">
                    {preview.totals?.computed?.net_amount ?? "-"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Commit form */}
          <div className="mt-6 bg-gray-50 p-4 rounded shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <select
                value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                className="border px-3 py-2 rounded"
              >
                <option value="">-- Select Customer --</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <input
                type="text"
                placeholder="Invoice Number (optional)"
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                className="border px-3 py-2 rounded"
              />

              <input
                type="date"
                placeholder="Invoice Date (optional)"
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                className="border px-3 py-2 rounded"
              />

              <input
                type="number"
                placeholder="Created By (User ID)"
                value={form.created_by}
                onChange={(e) =>
                  setForm({ ...form, created_by: parseInt(e.target.value || "0", 10) })
                }
                className="border px-3 py-2 rounded"
              />
            </div>

            <button
              onClick={handleCommit}
              className="bg-green-600 text-white px-4 py-2 rounded mt-4"
              disabled={loading}
            >
              {loading ? "Saving..." : "Commit to DB"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
